/**
 * Zenio Strap Finder Modal Client JS (Shopify Edition)
 * Works seamlessly with Shopify Theme Assets CDN & Vision Theme
 */

class ZenioShopifyStrapFinderModal {
	constructor() {
		this.brandsIndex = [];
		this.brandCache = {};
		this.defaultWatches = [];
		this.selectedBrand = '';
		this.selectedCollection = '';
		this.searchQuery = '';
		this.watches = [];
		this.filteredWatches = [];
		this.isOpen = false;
		this.isLoading = false;

		this.init();
	}

	async init() {
		this.bindEvents();
		await this.loadBrandsIndex();
		await this.loadDefaultWatches();
	}

	async loadBrandsIndex() {
		try {
			let indexUrl = window.ZenioAssetsUrl ? window.ZenioAssetsUrl('watch-brands-index.json') : '/assets/watch-brands-index.json';
			indexUrl += (indexUrl.indexOf('?') !== -1 ? '&_t=' : '?_t=') + Date.now();
			const res = await fetch(indexUrl);
			if (!res.ok) throw new Error('Failed to load brands index');
			this.brandsIndex = await res.json();
			this.renderFilters();
		} catch (err) {
			console.error('[Zenio Strap Finder] Error loading brands index:', err);
		}
	}

	async loadBrandData(brandSlug) {
		if (this.brandCache[brandSlug]) {
			return this.brandCache[brandSlug];
		}

		try {
			const assetFile = `watch-brand-${brandSlug}.json`;
			let url = window.ZenioAssetsUrl ? window.ZenioAssetsUrl(assetFile) : `/assets/${assetFile}`;
			url += (url.indexOf('?') !== -1 ? '&_t=' : '?_t=') + Date.now();
			const res = await fetch(url);
			if (!res.ok) throw new Error(`Failed to load brand data: ${brandSlug}`);
			const data = await res.json();
			this.brandCache[brandSlug] = data;
			return data;
		} catch (err) {
			console.error(`[Zenio Strap Finder] Error loading brand ${brandSlug}:`, err);
			return [];
		}
	}

	async loadDefaultWatches() {
		this.setLoading(true);
		const popularSlugs = ['rolex', 'omega', 'cartier', 'audemars-piguet', 'patek-philippe'];
		let collected = [];

		for (const slug of popularSlugs) {
			if (collected.length >= 24) break;
			const data = await this.loadBrandData(slug);
			if (Array.isArray(data)) {
				const tagged = data.map((item) => ({ ...item, brandSlug: slug }));
				collected = collected.concat(tagged);
			}
		}

		this.defaultWatches = collected.slice(0, 24);
		this.setLoading(false);

		if (!this.selectedBrand && !this.searchQuery) {
			this.filteredWatches = this.defaultWatches;
			this.renderWatches();
		}
	}

	bindEvents() {
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('[data-zenio-strap-finder-trigger]');
			if (trigger) {
				e.preventDefault();
				this.open();
			}

			const closeBtn = e.target.closest('.zenio-modal__close, .zenio-modal__backdrop');
			if (closeBtn && this.isOpen) {
				e.preventDefault();
				this.close();
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.isOpen) {
				this.close();
			}
		});
	}

	open() {
		const modal = document.getElementById('zenio-strap-finder-modal');
		if (!modal) return;
		this.isOpen = true;
		modal.classList.add('zenio-modal--active');
		document.body.style.overflow = 'hidden';

		if (!this.selectedBrand && !this.searchQuery && this.defaultWatches.length > 0) {
			this.filteredWatches = this.defaultWatches;
			this.renderWatches();
		}
	}

	close() {
		const modal = document.getElementById('zenio-strap-finder-modal');
		if (!modal) return;
		this.isOpen = false;
		modal.classList.remove('zenio-modal--active');
		document.body.style.overflow = '';
	}

	setLoading(loading) {
		this.isLoading = loading;
		const grid = document.getElementById('zenio-watches-grid');
		if (!grid) return;
		if (loading) {
			const skeletonCard = `
				<div class="zenio-modal__skeleton-card">
					<div class="zenio-modal__skeleton-img"></div>
					<div class="zenio-modal__skeleton-body">
						<div class="zenio-modal__skeleton-line zenio-modal__skeleton-line--title"></div>
						<div class="zenio-modal__skeleton-line zenio-modal__skeleton-line--ref"></div>
						<div class="zenio-modal__skeleton-pills">
							<div class="zenio-modal__skeleton-pill"></div>
							<div class="zenio-modal__skeleton-pill"></div>
						</div>
					</div>
				</div>
			`;
			grid.innerHTML = `
				<div class="zenio-modal__skeleton-grid">
					${Array(8).fill(skeletonCard).join('')}
				</div>
			`;
		}
	}

	renderFilters() {
		const brandSelect = document.getElementById('zenio-brand-select');
		if (!brandSelect) return;

		let html = '<option value="">All Brands</option>';
		
		const sortedBrands = [...this.brandsIndex].map(b => {
			const formattedName = b.name
				.replace(/_/g, ' ')
				.split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ');
			return { ...b, formattedName };
		}).sort((a, b) => a.formattedName.localeCompare(b.formattedName));

		sortedBrands.forEach((b) => {
			html += `<option value="${b.slug}">${b.formattedName} (${b.count})</option>`;
		});

		brandSelect.innerHTML = html;

		brandSelect.onchange = async (e) => {
			this.selectedBrand = e.target.value;
			this.selectedCollection = '';
			const collSelect = document.getElementById('zenio-collection-select');
			if (collSelect) collSelect.value = '';

			if (this.selectedBrand) {
				this.setLoading(true);
				this.watches = await this.loadBrandData(this.selectedBrand);
				this.setLoading(false);
				this.updateCollectionsList();
			} else {
				this.watches = [];
			}
			this.applyFilters();
		};

		const collSelect = document.getElementById('zenio-collection-select');
		if (collSelect) {
			collSelect.onchange = (e) => {
				this.selectedCollection = e.target.value;
				this.applyFilters();
			};
		}

		const searchInput = document.getElementById('zenio-search-input');
		if (searchInput) {
			searchInput.oninput = (e) => {
				this.searchQuery = e.target.value.toLowerCase().trim();
				this.applyFilters();
			};
		}

		const resetBtn = document.getElementById('zenio-reset-filters');
		if (resetBtn) {
			resetBtn.onclick = () => {
				this.selectedBrand = '';
				this.selectedCollection = '';
				this.searchQuery = '';
				this.watches = [];
				if (brandSelect) brandSelect.value = '';
				if (collSelect) collSelect.value = '';
				if (searchInput) searchInput.value = '';
				this.applyFilters();
			};
		}
	}

	updateCollectionsList() {
		const collSelect = document.getElementById('zenio-collection-select');
		if (!collSelect) return;

		const uniqueCollections = [...new Set(this.watches.map((w) => w.watch).filter(Boolean))];
		const sortedCollections = uniqueCollections.map(c => {
			const formattedName = c
				.replace(/_/g, ' ')
				.split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ');
			return { original: c, formattedName };
		}).sort((a, b) => a.formattedName.localeCompare(b.formattedName));

		let html = '<option value="">All Collections</option>';
		sortedCollections.forEach((c) => {
			html += `<option value="${c.original}">${c.formattedName}</option>`;
		});
		collSelect.innerHTML = html;
		collSelect.disabled = false;
	}

	applyFilters() {
		if (!this.selectedBrand) {
			if (this.searchQuery) {
				// Search across default items
				this.filteredWatches = this.defaultWatches.filter((w) => {
					const fullStr = `${w.brand} ${w.watch} ${w.model} ${w.yearRef}`.toLowerCase();
					return fullStr.includes(this.searchQuery);
				});
			} else {
				this.filteredWatches = this.defaultWatches;
			}
			this.renderWatches();
			return;
		}

		this.filteredWatches = this.watches.filter((w) => {
			if (this.selectedCollection && w.watch !== this.selectedCollection) {
				return false;
			}
			if (this.searchQuery) {
				const fullStr = `${w.brand} ${w.watch} ${w.model} ${w.yearRef}`.toLowerCase();
				if (!fullStr.includes(this.searchQuery)) {
					return false;
				}
			}
			return true;
		});

		this.renderWatches();
	}

	renderWatches() {
		const grid = document.getElementById('zenio-watches-grid');
		if (!grid) return;

		const list = this.filteredWatches.length > 0 ? this.filteredWatches : (!this.selectedBrand && !this.searchQuery ? this.defaultWatches : []);

		if (list.length === 0) {
			grid.innerHTML = `
				<div class="zenio-modal__error-box">
					<p class="zenio-modal__error-title">No Matching Models Found</p>
					<p class="zenio-modal__error-message">Try adjusting your filters or search keywords.</p>
				</div>
			`;
			return;
		}

		let cardsHtml = '';
		list.forEach((w) => {
			// Priority: yearSrc (watch WITH strap as requested by user), then watchFace
			const imgUrl = w.yearSrc || w.watchFace || '';
			const imgTag = imgUrl
				? `<img src="${imgUrl}" alt="${w.brand} ${w.model}" class="zenio-modal__card-img" loading="lazy" />`
				: '<div style="height: 110px; background: #f0f0f0; border-radius: 8px; margin-bottom: 10px;"></div>';

			const lugBadge = w.lugWidth && w.lugWidth !== '-' ? `<span class="zenio-modal__card-spec">${w.lugWidth}mm Lug</span>` : '';
			const buckleBadge = w.buckleWidth && w.buckleWidth !== '-' ? `<span class="zenio-modal__card-spec zenio-modal__card-spec--buckle">${w.buckleWidth}mm Buckle</span>` : '';

			const brandSlug = w.brandSlug || this.selectedBrand || (w.brand ? w.brand.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'rolex');
			const slug = w.seoSlug || w.slug || (w.model ? w.model.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : '');
			const urlParams = new URLSearchParams(window.location.search);
			const previewThemeId = urlParams.get('preview_theme_id');
			
			// Default URL
			let targetUrl = `/pages/strap-detail?brand=${encodeURIComponent(brandSlug)}&slug=${encodeURIComponent(slug)}`;
			
			// Check if brand has pre-generated pages
			const preGeneratedBrands = (window.ZenioStrapFinderConfig?.preGeneratedBrands || []).map(b => String(b).toLowerCase().trim());
			if (preGeneratedBrands.includes(brandSlug.toLowerCase())) {
				targetUrl = `/pages/${encodeURIComponent(slug)}`;
			}

			if (previewThemeId) {
				targetUrl += (targetUrl.includes('?') ? '&' : '?') + `preview_theme_id=${encodeURIComponent(previewThemeId)}`;
			}

			cardsHtml += `
				<a href="${targetUrl}" class="zenio-modal__watch-card">
					${imgTag}
					<div class="zenio-modal__card-body">
						<h5 class="zenio-modal__card-title">${w.model}</h5>
						<p class="zenio-modal__card-ref">Ref ${w.yearRef}</p>
						<div class="zenio-modal__card-specs">
							${lugBadge}
							${buckleBadge}
						</div>
					</div>
				</a>
			`;
		});

		grid.innerHTML = cardsHtml;

		// Attach instant hover prefetching for sub-100ms load
		grid.querySelectorAll('.zenio-modal__watch-card').forEach((card) => {
			card.addEventListener('mouseenter', () => {
				if (!card.dataset.prefetched && card.href) {
					card.dataset.prefetched = 'true';
					const link = document.createElement('link');
					link.rel = 'prefetch';
					link.href = card.href;
					document.head.appendChild(link);
				}
			}, { passive: true });
		});

		// Attach fade-in for images
		grid.querySelectorAll('.zenio-modal__card-img').forEach((img) => {
			if (img.complete) {
				img.classList.add('zenio-modal__card-img--loaded');
			} else {
				img.onload = () => img.classList.add('zenio-modal__card-img--loaded');
			}
		});
	}
}

document.addEventListener('DOMContentLoaded', () => {
	window.zenioStrapFinderModal = new ZenioShopifyStrapFinderModal();
});
